import { useEffect, useState } from 'react';
import { Provider as StoreProvider, useDispatch, useSelector } from 'react-redux';
import { useResizeObserver, useDebounce } from '@services/hooks.service';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { bbox } from '@turf/turf';
import {
  Button, Icon, Search, Tooltip, 
} from '@nazka/nazka.mapframe.components';
import {
  faBuilding,
  faGalaxy,
  faHome,
  faMessages,
  faPro,
  faRectanglePro,
  faShuffle,
} from '@fortawesome/pro-solid-svg-icons';

import { useRoute, useVacancy, useAddressSugestions } from '@services/query.service';
import {
  getTransportMode, formatDistance, shouldUseDateForTransportType, 
  formatTime,
} from '@services/utils.service';
import { updateTransportDateTime } from '@App/pages/Filter/filter.slice';
import { setUseJobApplicationLocation } from '@App/pages/JobVacancy/job-vacancy.slice';
import { queryClient } from '@App/app.query-client';
import store from '@App/app.store';
import { setCurrentRouteInfoPanelHeight, setHomeAddress } from '@App/components/Map/map.slice';
import { useCurrentJobDestination } from '@App/components/SharedCompanyInfo/NearbyCompanySection/nearby-company-section.utils';
import RouteDateTimeSelect from './RouteDateTimeSelect/RouteDateTimeSelect';
import RouteDirections from './RouteDirections/RouteDirections';

import {
  formatCompanyAddress,
  formatApplicationAddress,
  getJobApplicationLocation,
  getSelectedVacancy,
  getGoogleMapsUrl,
  fitBounds,
} from './route-info.utils';

import './route-info.styl';
import { faCircleInfo, faWarning } from '@fortawesome/pro-regular-svg-icons';

function RouteInfo({ map, isJobCoach, forPrint }) {
  const { size: { height, width }, elementRef: routeInfoRef } = useResizeObserver();

  const transportMode = useSelector(state => state.map.transportMode);
  const homeAddress = useSelector(state => state.map.homeAddress);
  const { OIDN, vdabReferentie, useJobApplicationLocation } = useSelector(state => state.vacancy);
  const transportDateTime = useSelector(state => state.filter.transportDateTime);
  const currentRouteInfoPanelHeight = useSelector(state => state.map.currentRouteInfoPanelHeight);

  const [addressSearchValue, setAddressSearchValue] = useState('');

  const dispatch = useDispatch();

  const { data: company, isLoading: isLoadingCompany } = useVacancy(OIDN);

  const selectedVacancy = getSelectedVacancy(company?.properties?.vacancies, vdabReferentie);
  const jobApplicationLocation = getJobApplicationLocation(selectedVacancy);
  const toCoordinates = useCurrentJobDestination(company, vdabReferentie);

  const homeAddressCoordinates = { lat: homeAddress?.center[1], lng: homeAddress?.center[0] };

  const { data: route, isLoading, isError } = useRoute(
    homeAddressCoordinates,
    toCoordinates,
    getTransportMode(transportMode),
    shouldUseDateForTransportType(transportMode) ? transportDateTime : undefined,
  );

  const debouncedFitBounds = useDebounce((bounds, bottom) => {
    map.fitBounds(bounds, {
      padding: {
        top: 100,
        bottom: forPrint ? 100 : bottom + 50,
        left: 100,
        right: 100,
      },
      duration: forPrint ? 0 : 1000,
      maxZoom: 20,
    });
  }, 300);

  useEffect(() => {
    if (!forPrint) dispatch(setCurrentRouteInfoPanelHeight(height));
  }, [dispatch, height, forPrint]);

  useEffect(() => {
    if (map && route?.route && currentRouteInfoPanelHeight !== 0) {
      map.getSource('vacancy-route').setData({
        ...route?.route,
        features: route?.route?.features.map((f) => {
          let { mode } = f?.properties || {};
          if (!mode) {
            mode = f?.weight_name;
          } else if (mode && mode === 'BICYCLE' && transportMode === 'e-bike') {
            mode = 'E-BIKE';
          }

          return {
            ...f,
            properties: {
              ...f?.properties,
              mode,
            },
          };
        }),
      });
      const bounds = bbox(route?.route);
      debouncedFitBounds(bounds, currentRouteInfoPanelHeight);
    }

    return () => {
      if (map) {
        map.getSource('vacancy-route').setData({ type: 'FeatureCollection', features: [] });
      }
    };
  }, [map, route, currentRouteInfoPanelHeight, width, debouncedFitBounds, transportMode]);

  const { sollicitatieAdres } = selectedVacancy?.properties?.sollicitatieprocedure || {};

  const {
    data: addressSugestions,
    isLoading: addressSugestionsLoading,
  } = useAddressSugestions(addressSearchValue);

  const jobIcon = company?.properties?.isJobAggregator ? '/img/icons/intrim.svg' : faBuilding;
  const jobIconCSS = company?.properties?.isJobAggregator ? { transform: 'scale(1.3) translateX(2px)' } : {};

  return (
    <div 
      className={`route-info${(isLoading || isLoadingCompany) ? ' is-loading' : ''}${forPrint ? 'route-info--print' : ''}`}
      ref={routeInfoRef}
    >
      <div className="route-info__header">
        <div className="route-info__header__address">
          <div className="route-info__header__address__home">
            <Icon className="route-info__header__address__home__icon" icon={faHome} size="18px" />
            {homeAddress?.place_name ? homeAddress?.place_name : (
              <Search
                className="route-info__header__address__home__search"
                placeholder={(
                'Vul je adres in om de route naar deze '
                + `${useJobApplicationLocation ? 'sollicitatieadres' : 'werkgever'}`
                + ' te berekenen'
              )}
                options={addressSugestions || []}
                onSearch={v => setAddressSearchValue(v)}
                onSelect={(s) => {
                  if (!forPrint) dispatch(setHomeAddress(s));
                }}
                hideSearchIcon
                clearSearchInputOnBlur
                isLoading={addressSugestionsLoading}
                value={homeAddress ? { id: homeAddress?.id, label: homeAddress?.place_name } : null}
              />
            )}
          </div>
          <div className="route-info__header__address__company">
            <Icon
              className="route-info__header__address__company__icon"
              icon={useJobApplicationLocation ? faMessages : jobIcon}
              css={useJobApplicationLocation ? { transform: 'scale(1.25) translateX(2px)' } : jobIconCSS}
              size={useJobApplicationLocation ? '16px' : '18px'}
            />
            {useJobApplicationLocation && formatApplicationAddress(sollicitatieAdres)}
            {!useJobApplicationLocation && (toCoordinates 
              ? formatCompanyAddress(company) 
              : (
                <span className="route-info__header__address__company__no-address">
                  {company?.properties?.CRAB_GEM 
                    ? `Het exacte adres van deze job is niet gekend, maar is in de gemeente ${company.properties.CRAB_GEM}.`
                    : 'Het exacte adres van deze job is niet gekend.'}
                </span>
              )
            )}
            {company?.properties?.isJobAggregator && (
              <Tooltip
                id="job-aggregator-tooltip"
                clickable
                tooltip={(
                  <div className="tooltip">
                    {toCoordinates && 'Dit is het adres van het jobkantoor. Het adres van de job is niet bekend bij ons.'}
                    {!toCoordinates && 'Deze job wordt aangeboden door een jobkantoor.'}
                  </div>
                )}
              >
                <Icon
                  icon={faWarning}
                  size="14px"
                  rootElProps={{ 'data-tooltip-id': 'job-aggregator-tooltip' }}
                  className="route-info__header__address__company__tooltip__icon"
                />
              </Tooltip>
            )}
          </div>
          {isJobCoach && jobApplicationLocation && !forPrint && (
            <Button
              className="route-info__header__address__swap"
              icon={faShuffle}
              layout="ghost"
              hover={false}
              onClick={() => {
                dispatch(setUseJobApplicationLocation(!useJobApplicationLocation));
                window.gtag?.('event', 'map_click_show_route_to_job_application_address_button', {
                  event_category: 'Map',
                  event_label: 'click show route to job application address button',
                }); 
              }}
            >
              Toon de route naar het
              {useJobApplicationLocation ? ' bedrijf' : ' sollicitatieadres'}
              <Icon
                className="route-info__header__address__swap__text__pro"
                icon={faRectanglePro}
              />
            </Button>
          )}
        </div>
        {homeAddress && (
        <div className="route-info__header__distance">
          {(isError || !route?.routeInformation) ? (
            <div className="route-info__header__distance__text route-info__header__distance__text--error">
              <p>We konden geen route vinden.</p>
            </div>
          ) : (
            <>
              <div
                className="route-info__header__distance__total-distance"
                onClick={() => {
                  const bounds = bbox(route?.route);
                  if (map && bounds) {
                    fitBounds(map, bounds, currentRouteInfoPanelHeight, forPrint);
                  }
                }}
              >
                <p className="route-info__header__distance__total-distance__text">
                  <span>{'Afstand: '}</span>
                  {formatDistance(route.routeInformation.distance)}
                  {' / '}
                  {formatTime(route.routeInformation.duration)}
                </p>
                {!['walking', 'bike', 'e-bike', 'car'].includes(transportMode) && (
                  <p className="route-info__header__distance__total-distance__text">
                    <span>{'Vertrektijd: '}</span>
                    <RouteDateTimeSelect 
                      dateTime={transportDateTime}
                      setDateTime={v => dispatch(updateTransportDateTime(v))}
                    />
                  </p>
                )}
              </div>
              {isJobCoach && (
                <Button
                  icon="/img/logo/google.svg"
                  iconSize="20px"
                  rootElProps={{ 
                    title: 'Bekijk route op Google Maps',
                    onClick: () => {
                      window.gtag?.('event', 'map_click_open_routing_on_google_maps_button', {
                        event_category: 'Map',
                        event_label: 'click open routing on Google maps button',
                      });
                    },
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }}
                  href={getGoogleMapsUrl(homeAddressCoordinates, toCoordinates, transportMode)}
                  className="route-info__header__distance__button"
                  disabled={['car_ov', 'bike_ov', 'e-bike'].includes(transportMode)}
                >
                  <Icon
                    size="15px" 
                    icon={faPro}
                    className="route-info__header__distance__button__pro"
                  />
                </Button>
              )}
            </>
          )}
        </div>
        )}
      </div>
      {route?.route?.features && (<RouteDirections map={map} />)}
    </div>
  );
}

class RouteInfoControl {
  constructor(isJobCoach, forPrint) {
    this.isJobCoach = isJobCoach;
    this.forPrint = forPrint;
  }

  onAdd(map) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'maplibregl-ctrl route-info-control';
    if (this.forPrint) this._container.style.display = 'none';
    this._root = createRoot(this._container);
    this._root.render(
      <StoreProvider store={store}>
        <QueryClientProvider client={queryClient}>
          <RouteInfo map={map} isJobCoach={this.isJobCoach} forPrint={this.forPrint} />
        </QueryClientProvider>
      </StoreProvider>,
    );
    return this._container;
  }

  onRemove() {
    this._container?.parentNode.removeChild(this._container);
    this._map = undefined;
  }
}

export { RouteInfo };
export default RouteInfoControl;